/**
 * @author        tae@zumper.com (Tae Yoon)
 * @copyright     Copyright (c) 2019, Zumper
 * @description   trackMultiMessageEvent thunk
 */

import { AnalyticsC, Origin } from '@zcode/nr-utils'
import type { Listable, MaybeEmptyProperty } from '@zcode/state-type'
import type { MaybeEmpty } from '@zcode/utility-types'
import type { Dispatch } from 'redux'
import {
  type AdditionalProps,
  type TrackDetailEventProps,
  trackDetailEvent,
} from './trackDetailEvent'
import {
  type TrackDetailSimilarEventProps,
  trackDetailSimilarEvent,
} from './trackDetailSimilarEvent'
import { type TrackListItemEventProps, trackListItemEvent } from './trackListItemEvent'

export interface TrackMultiMessageEventProps {
  analyticChannel:
    | typeof AnalyticsC.AFFORDABILITY_CALCULATOR
    | typeof AnalyticsC.FULL_DETAIL
    | typeof AnalyticsC.LIST
    | typeof AnalyticsC.LIST_ITEM
    | typeof AnalyticsC.LIST_ITEM_MESSAGED
    | typeof AnalyticsC.PERMANENT_ADDRESS
    | typeof AnalyticsC.PROGRAMMATIC_RENT
    | typeof AnalyticsC.HOMEPAGE
  additionalProps: Omit<Partial<AdditionalProps>, 'similarProperty' | 'shownSimilarPropertyIds'>
  affordabilityCalculatorEvent: TrackListItemEventProps['name']
  detailEvent: TrackDetailEventProps['name']
  detailSimilarEvent: TrackDetailSimilarEventProps['name']
  entity: MaybeEmpty<Listable> | MaybeEmptyProperty
  listItemEvent: TrackListItemEventProps['name']
  origin: Origin
  programmaticRentEvent: TrackListItemEventProps['name']
}

export const trackMultiMessageEvent =
  ({
    additionalProps,
    affordabilityCalculatorEvent,
    analyticChannel,
    detailEvent,
    detailSimilarEvent,
    entity,
    listItemEvent,
    origin,
    programmaticRentEvent,
  }: TrackMultiMessageEventProps) =>
  (dispatch: Dispatch) => {
    switch (analyticChannel) {
      case AnalyticsC.FULL_DETAIL:
      case AnalyticsC.PERMANENT_ADDRESS: {
        if (origin === Origin.SIMILAR) {
          dispatch(
            trackDetailSimilarEvent({
              name: detailSimilarEvent,
              similarEntity: entity,
              additionalProps,
            })
          )
        } else {
          dispatch(trackDetailEvent({ name: detailEvent, entity, additionalProps }))
        }
        break
      }
      case AnalyticsC.LIST:
      case AnalyticsC.LIST_ITEM:
      case AnalyticsC.LIST_ITEM_MESSAGED:
        dispatch(trackListItemEvent({ name: listItemEvent, entity, additionalProps }))
        break
      case AnalyticsC.AFFORDABILITY_CALCULATOR:
        dispatch(
          trackListItemEvent({ name: affordabilityCalculatorEvent, entity, additionalProps })
        )
        break
      case AnalyticsC.PROGRAMMATIC_RENT:
        dispatch(trackListItemEvent({ name: programmaticRentEvent, entity, additionalProps }))
        break
      default:
        return analyticChannel
    }
  }
